import { LoginCredentials, LoginResponse, User, Tokens, SessionValidationResponse, RefreshTokenResponse } from "../types";
import { API_BASE_URL } from "../config/api";
import { logger } from "../utils/logger";

// Helper function to generate avatar URL
const generateAvatarUrl = (username: string): string => {
  const avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(
    username
  )}&background=f47842&color=fff&size=128`;
  logger.debug("Generated avatar URL:", avatarUrl);
  return avatarUrl;
};

// Helper function to decode JWT payload (for token expiration check only)
const decodeJWT = (token: string): any => {
  try {
    const payload = token.split(".")[1];
    const decoded = atob(payload);
    return JSON.parse(decoded);
  } catch (error) {
    throw new Error("Invalid token format");
  }
};

export const authService = {
  login: async (
    credentials: LoginCredentials
  ): Promise<{ user: User; tokens: Tokens }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: credentials.username,
          pin: credentials.password, // Backend expects 'pin' not 'password'
        }),
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Login failed" }));
        throw new Error(errorData.message || "Invalid credentials");
      }

      const data: LoginResponse = await response.json();
      logger.debug("Login response data:", data);

      if (!data.success) {
        throw new Error(data.message || "Login failed");
      }

      // Create user object with avatar
      const userWithAvatar = {
        ...data.data.user,
        avatar: generateAvatarUrl(data.data.user.username),
      };

      logger.debug("User with avatar:", userWithAvatar);
      logger.debug("Tokens:", data.data.tokens);

      // Calculate ExpiresAt (current time + ExpiresIn seconds)
      const expiresAt = Date.now() + data.data.tokens.ExpiresIn * 1000;

      // Store user data and tokens in localStorage
      localStorage.setItem("adminUser", JSON.stringify(userWithAvatar));
      localStorage.setItem("adminTokens", JSON.stringify(data.data.tokens));
      localStorage.setItem("adminToken", data.data.tokens.IdToken); // Use IdToken for protected endpoints
      localStorage.setItem("adminExpiresAt", expiresAt.toString());

      return {
        user: userWithAvatar,
        tokens: data.data.tokens,
      };
    } catch (error) {
      logger.error("Login error");
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("Network error occurred");
    }
  },

  validateSession: async (accessToken: string): Promise<{ user: User; isValid: boolean }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/validate-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          accessToken: accessToken,
        }),
      });

      if (!response.ok) {
        throw new Error("Session validation failed");
      }

      const data: SessionValidationResponse = await response.json();
      logger.debug("Session validation response:", data);

      if (!data.isValid || !data.user) {
        throw new Error("Invalid session");
      }

      // Create user object with avatar
      const userWithAvatar = {
        ...data.user,
        avatar: generateAvatarUrl(data.user.username),
      };

      // Update stored user data
      localStorage.setItem("adminUser", JSON.stringify(userWithAvatar));

      return {
        user: userWithAvatar,
        isValid: data.isValid,
      };
    } catch (error) {
      logger.error("Session validation error");
      throw new Error("Session validation failed");
    }
  },

  verifyToken: async (token: string): Promise<User> => {
    try {
      // First try to validate session with server
      try {
        const result = await authService.validateSession(token);
        return result.user;
      } catch (sessionError) {
        logger.warn("Session validation failed, falling back to local validation");
      }

      // Fallback to local token validation
      const decoded = decodeJWT(token);
      const currentTime = Math.floor(Date.now() / 1000);

      if (decoded.exp && decoded.exp < currentTime) {
        throw new Error("Token expired");
      }

      // Get user info from stored login response data
      const storedUserData = localStorage.getItem("adminUser");
      if (!storedUserData) {
        throw new Error("No user data found");
      }

      const userData: User = JSON.parse(storedUserData);
      logger.debug("Retrieved user data:", userData);

      // Ensure avatar is present (fallback if somehow missing)
      if (!userData.avatar) {
        userData.avatar = generateAvatarUrl(userData.username);
        localStorage.setItem("adminUser", JSON.stringify(userData));
      }

      return userData;
    } catch (error) {
      logger.error("Token verification error");
      throw new Error("Invalid token");
    }
  },

  logout: () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminTokens");
    localStorage.removeItem("adminUser");
    localStorage.removeItem("adminExpiresAt");
  },

  refreshToken: async (refreshToken: string, username?: string): Promise<{ accessToken: string; idToken?: string; username?: string; expiresIn?: number }> => {
    try {
      // Get username from stored user data if not provided
      const storedUser = localStorage.getItem("adminUser");
      const user = storedUser ? JSON.parse(storedUser) : null;
      const usernameToUse = username || user?.username;

      const response = await fetch(`${API_BASE_URL}/auth/refresh-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          refreshToken: refreshToken,
          ...(usernameToUse && { username: usernameToUse }),
        }),
      });

      if (!response.ok) {
        throw new Error("Token refresh failed");
      }

      const data: RefreshTokenResponse = await response.json();
      logger.debug("Token refresh response:", data);

      if (!data.accessToken) {
        throw new Error("No access token received");
      }

      // Update stored tokens with new accessToken and idToken if provided
      const storedTokens = localStorage.getItem("adminTokens");
      if (storedTokens) {
        const tokens: Tokens = JSON.parse(storedTokens);
        tokens.AccessToken = data.accessToken;
        // Note: Backend might also return idToken in refresh response
        if ((data as any).idToken) {
          tokens.IdToken = (data as any).idToken;
        }
        localStorage.setItem("adminTokens", JSON.stringify(tokens));
        localStorage.setItem("adminToken", tokens.IdToken); // Use IdToken for protected endpoints
        
        // Update ExpiresAt if ExpiresIn is provided
        if ((data as any).expiresIn) {
          const expiresAt = Date.now() + (data as any).expiresIn * 1000;
          localStorage.setItem("adminExpiresAt", expiresAt.toString());
        }
      }

      return {
        accessToken: data.accessToken,
        username: data.username,
      };
    } catch (error) {
      logger.error("Token refresh error");
      throw new Error("Token refresh failed");
    }
  },

  getStoredTokens: (): Tokens | null => {
    try {
      const storedTokens = localStorage.getItem("adminTokens");
      return storedTokens ? JSON.parse(storedTokens) : null;
    } catch (error) {
      logger.error("Error getting stored tokens");
      return null;
    }
  },

  isTokenExpired: (token: string): boolean => {
    try {
      const decoded = decodeJWT(token);
      const currentTime = Math.floor(Date.now() / 1000);
      return decoded.exp && decoded.exp < currentTime;
    } catch (error) {
      logger.error("Error checking token expiration");
      return true;
    }
  },

  // Check if token is expired or will expire within 30 seconds
  isTokenExpiringSoon: (token?: string): boolean => {
    try {
      // First check stored ExpiresAt
      const expiresAtStr = localStorage.getItem("adminExpiresAt");
      if (expiresAtStr) {
        const expiresAt = parseInt(expiresAtStr, 10);
        const bufferTime = 30 * 1000; // 30 seconds buffer
        return Date.now() > expiresAt - bufferTime;
      }

      // Fallback to token decoding
      if (token) {
        const decoded = decodeJWT(token);
        const currentTime = Math.floor(Date.now() / 1000);
        const bufferSeconds = 30;
        return decoded.exp && decoded.exp < currentTime + bufferSeconds;
      }

      return true;
    } catch (error) {
      logger.error("Error checking token expiration");
      return true;
    }
  },

  // Get authorization header with IdToken
  getAuthHeader: (): { Authorization: string } | {} => {
    try {
      const storedTokens = authService.getStoredTokens();
      if (storedTokens?.IdToken) {
        return { Authorization: `Bearer ${storedTokens.IdToken}` };
      }
      return {};
    } catch (error) {
      logger.error("Error getting auth header");
      return {};
    }
  },
};